import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { MapPin, Phone, Mail, Clock, Send, Loader2, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { API } from '../App';

const Contact = () => {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, formData);
      
      setSubmitted(true);
    } catch (error) {
      console.error('Error submitting contact form:', error);
      alert('Error submitting form. Please try again.');
    }
    setLoading(false);
  };

  const locations = [
    {
      city: 'Warehouse',
      state: 'WA',
      address: '18/92-94 Poole St, Welshpool WA 6106',
      phone: '08 6187 5523',
      isHQ: true
    }
  ];

  return (
    <>
      <Helmet>
        <link rel="canonical" href="https://www.extreme-clean.com.au/contact" />
        <title>Contact Us | Extreme Clean Australia</title>
      </Helmet>
      <div className="pt-24 lg:pt-44" data-testid="contact-page">
      {/* Hero */}
      <section className="bg-[#273268] py-24" data-testid="contact-hero">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-[#3B82F6] font-mono text-sm uppercase tracking-wider mb-4">Contact Us</p>
            <h1 className="font-heading font-bold text-5xl md:text-6xl leading-snug mb-6 text-white">
              Get In Touch
            </h1>
            <p className="text-gray-400 text-lg">
              Ready to experience spotless spaces? Contact us for a free quote or to discuss 
              your cleaning requirements.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Info Bar */}
      <section className="bg-[#3B82F6] py-6" data-testid="contact-bar">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-6">
            <a href="tel:0861875523" className="flex items-center gap-3 text-white">
              <Phone size={20} />
              <span className="font-mono">08 6187 5523</span>
            </a>
            <a href="mailto:info.eca@extreme-clean.com.au" className="flex items-center gap-3 text-white">
              <Mail size={20} />
              <span>info.eca@extreme-clean.com.au</span>
            </a>
            <div className="flex items-center gap-3 text-white">
              <Clock size={20} />
              <span>Mon - Fri: 8AM - 5PM</span>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-24" data-testid="contact-form-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Form */}
            <div>
              <h2 className="font-heading font-bold text-3xl mb-8">Send Us a Message</h2>
              
              {submitted ? (
                <div className="bg-[#10B981] bg-opacity-10 border border-[#10B981] p-8 text-center animate-fadeIn" data-testid="contact-success">
                  <CheckCircle size={48} className="text-[#10B981] mx-auto mb-4" />
                  <h3 className="font-heading font-bold text-xl mb-2">Message Sent!</h3>
                  <p className="text-gray-600">
                    Thank you for contacting us. We'll get back to you within 24 hours.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                  <div>
                    <label className="form-label">Full Name *</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      className="form-input"
                      placeholder="Your full name"
                      required
                      data-testid="contact-name-input"
                    />
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="form-label">Email Address *</label>
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        className="form-input"
                        placeholder="your@email.com"
                        required
                        data-testid="contact-email-input"
                      />
                    </div>
                    <div>
                      <label className="form-label">Phone Number</label>
                      <input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({...formData, phone: e.target.value})}
                        className="form-input"
                        placeholder="0400 000 000"
                        data-testid="contact-phone-input"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="form-label">Message *</label>
                    <textarea
                      value={formData.message}
                      onChange={(e) => setFormData({...formData, message: e.target.value})}
                      className="form-input resize-none"
                      rows={6}
                      placeholder="Tell us about your cleaning requirements..."
                      required
                      data-testid="contact-message-input"
                    />
                  </div>
                  
                  <button
                    type="submit"
                    disabled={loading}
                    className="btn-primary flex items-center gap-2"
                    data-testid="contact-submit-btn"
                  >
                    {loading ? (
                      <Loader2 className="animate-spin" size={18} />
                    ) : (
                      <>
                        <Send size={18} />
                        Send Message
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>

            {/* Info */}
            <div>
              <h2 className="font-heading font-bold text-3xl mb-8">Our Locations</h2>
              
              <div className="space-y-6">
                {locations.map((location) => (
                  <div 
                    key={location.city}
                    className="p-6 bg-[#3B82F6]"
                    data-testid={`location-info-${location.city.toLowerCase()}`}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="font-heading font-bold text-xl text-white">{location.city}</h3>
                        <p className="text-blue-100 text-sm">
                          {location.state}
                        </p>
                      </div>
                      {location.isHQ && (
                        <span className="bg-white text-[#3B82F6] text-xs px-2 py-1 uppercase tracking-wider font-bold">
                          HQ
                        </span>
                      )}
                    </div>
                    <div className="space-y-3">
                      <div className="flex items-center gap-3">
                        <MapPin size={18} className="text-white" />
                        <span className="text-sm text-blue-100">
                          {location.address}
                        </span>
                      </div>
                      <div className="flex items-center gap-3">
                        <Phone size={18} className="text-white" />
                        <span className="text-sm font-mono text-blue-100">
                          {location.phone}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
                
                {/* Service Area Note */}
                <div className="p-6 bg-gray-50 border-l-4 border-[#3B82F6]">
                  <p className="font-heading font-bold text-lg mb-2">Service Area</p>
                  <p className="text-gray-600 text-sm">
                    We proudly serve clients across the entire Western Australia region.
                  </p>
                </div>
              </div>

              {/* Hours */}
              <div className="mt-8 p-6 border-2 border-[#0A0A0A]">
                <h3 className="font-heading font-bold text-xl mb-4">Business Hours</h3>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Monday - Friday</span>
                    <span className="font-mono">8:00 AM - 5:00 PM</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Saturday & Sunday</span>
                    <span className="font-mono">Closed</span>
                  </div>
                </div>
                <p className="text-xs text-neutral-500 mt-4">
                  * Cleaning services available 24/7 by arrangement
                </p>
              </div>

              {/* Google Review Link */}
              <div className="mt-6">
                <a 
                  href="https://g.page/r/CfT5FOR97gMgEBM/review" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-[#3B82F6] hover:text-[#273268] font-semibold transition-colors"
                  data-testid="google-review-link"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Leave us a Google review
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Placeholder */}
      <section className="h-96 bg-gray-50 relative" data-testid="map-section">
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <MapPin size={48} className="text-[#3B82F6] mx-auto mb-4" />
            <p className="font-heading font-bold text-xl">Serving Western Australia</p>
            <p className="text-gray-600 text-sm mt-2">Professional cleaning services across the entire state</p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24" data-testid="faq-section">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-[#0A0A0A] font-mono text-sm uppercase tracking-wider mb-2">FAQ</p>
            <h2 className="font-heading font-bold text-4xl">Common Questions</h2>
          </div>

          <div className="space-y-4">
            {[
              {
                q: 'How quickly can you start?',
                a: 'We can usually commence services within 48-72 hours of receiving your booking confirmation.'
              },
              {
                q: 'Do you provide cleaning supplies?',
                a: 'Yes, we bring all necessary cleaning equipment and supplies. We use eco-friendly, commercial-grade products.'
              },
              {
                q: 'Are your staff insured?',
                a: 'Absolutely. All our staff are fully insured with $20M public liability coverage and workers compensation.'
              },
              {
                q: 'Can I get a customised cleaning schedule?',
                a: 'Yes, we offer flexible scheduling including daily, weekly, fortnightly, or monthly services tailored to your needs.'
              }
            ].map((item, index) => (
              <div key={index} className="border border-gray-200 p-6" data-testid={`faq-${index}`}>
                <h3 className="font-heading font-bold text-lg mb-2">{item.q}</h3>
                <p className="text-gray-600">{item.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-[#3B82F6]" data-testid="contact-cta">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="font-heading font-bold text-4xl md:text-5xl text-white mb-6">
            Prefer to Talk?
          </h2>
          <p className="text-blue-100 text-lg mb-8">
            Our team is ready to discuss your cleaning requirements and provide a free quote.
          </p>
          <a 
            href="tel:0861875523"
            className="bg-white text-[#0A0A0A] hover:bg-gray-100 transition-all uppercase font-bold tracking-widest px-8 py-4 text-sm inline-flex items-center gap-2"
            data-testid="call-cta-btn"
          >
            <Phone size={18} />
            Call 08 6187 5523
          </a>
        </div>
      </section>
    </div>
    </>
  );
};

export default Contact;
